import { useQuery } from "@tanstack/react-query";
import { ReactElement, useMemo } from "react";
import { api } from "../services/api";
import { PermitionContext } from "@/contexts/Permition";
import { menuItems } from "@/components/SideMenu";
import { useAuth } from "@/hooks/useAuth";

export type TUsuario = {
  ativo: boolean;
  codigo: string;
  cpf: string;
  datacadastro: string;
  email: string;
  empresa: number;
  id: number;
  login: string;
  nome: string;
  senha: string;
  setor: number;
  telefone: string;
  turno: number;
};

export type TPermissaoUsuario = {
  ativo: boolean;
  dataatualizacao: string;
  datageracao: string;
  dataliberacao: string;
  empresa: number;
  funcionalidade: number;
  id: number;
  rotina: {
    aplicacao: number;
    ativo: boolean;
    id: number;
    menuName: string;
    menu: number;
    nome: string;
    ordem: number;
    tipo: number;
  };
  usuario: {
    ativo: boolean;
    codigo: string;
    cpf: string;
    datacadastro: string;
    email: string;
    empresa: number;
    id: number;
    login: string;
    nome: string;
    senha: string;
    setor: number;
    telefone: string;
    turno: number;
  };
  usuarioliberacao: {
    ativo: boolean;
    codigo: string;
    cpf: string;
    datacadastro: string;
    email: string;
    empresa: number;
    id: number;
    login: string;
    nome: string;
    senha: string;
    setor: number;
    telefone: string;
    turno: number;
  };
};

export function PermittionProvider({ children }: { children: ReactElement }) {
  const { token } = useAuth();

  const {
    data: permissions,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userPermissions", token],
    queryFn: () => {
      if (token) {
        return api.get<TPermissaoUsuario[]>("permissao/usuario");
      }
      return Promise.resolve(undefined);
    },
    select: (data) => data?.data || [],
    enabled: !!token,
    staleTime: 1000 * 60 * 10,
    refetchInterval: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 10,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const menus = useMemo(() => {
    if (!permissions) return [];

    return permissions?.reduce<number[]>((prev, current) => {
      if (current?.ativo && !prev.includes(current?.rotina?.menu)) {
        return [...prev, current?.rotina?.menu];
      }
      return prev;
    }, []);
  }, [permissions]);

  const rotinas = permissions?.reduce<{ id: string; descricao: string }[]>(
    (prev, current) => {
      if (
        current?.ativo &&
        current?.rotina &&
        !prev.some((rotina) => rotina?.id === current?.rotina?.id.toString())
      ) {
        return [
          ...prev,
          {
            id: current?.rotina?.id.toString(),
            descricao: current?.rotina?.nome,
          },
        ];
      }
      return prev;
    },
    []
  );

  const filteredMenuItems = useMemo(() => {
    if (!menuItems) return [];

    let menusFilted = menuItems?.filter((item: any) => {
      return item?.id === 930 || menus?.includes(item?.id);
    });

    return menusFilted?.map((menu) => {
      return {
        ...menu,
        childrens:
          menu?.childrens?.filter?.((itemMenu) =>
            itemMenu?.id
              ? rotinas?.find((r) => +r.id == itemMenu?.id) ?? [
                  30, 21, 3, 2, 14,
                ]?.includes(itemMenu?.id) //itemMenu e uma rotina! e esses ids sao os que ainda nao foram mapeados adequadamentes. Remover apos preenchimento do banco adequadamente
              : true
          ) || [],
      };
    });
  }, [permissions, menus]);

  return (
    <PermitionContext.Provider
      value={{
        //@ts-ignore
        rotinas,
        menus: filteredMenuItems,
        isLoading,
        error,
        hasPermissions: permissions !== undefined && permissions?.length > 0,
      }}
    >
      {children}
    </PermitionContext.Provider>
  );
}
